import type { User } from 'oidc-client';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAuth } from '../context/auth.context';
import { UserActionTypes, useUser } from '../context/user.context';
import { DEFAULT_IDP } from '../helpers/constants';
import { isPublicComputerUser } from '../helpers/public-computer-claim';
import {
  beginPublicSession,
  clearLastSessionPublic,
  endPublicSession,
  purgeDurableCredentials,
  removeSessionItem,
  writeSessionItem,
} from '../helpers/session-scoped-storage';
import LoginLoader from './login-loader.component';

/** The idp carried through the OIDC `state` by `signinRedirect`, if present. */
const getIdpFromState = (state: unknown): string | undefined => {
  if (typeof state !== 'object' || state === null) return undefined;
  const idp = (state as { idp?: unknown }).idp;
  return typeof idp === 'string' ? idp : undefined;
};

const LoginCallback = () => {
  const { t } = useTranslation();
  const { state, dispatch } = useUser();
  const { signinRedirectCallback } = useAuth();
  const navigate = useNavigate();

  /**
   * Whether a session already existed at mount. Every login reaches here unauthenticated, so a
   * live session means the URL was replayed (session-restore, refresh, back-nav) — the library
   * rejects those (code already consumed), and wiping on that would drop a signed-in user onto
   * the login form. A ref, not `state.auth`, which in the effect deps would re-run the callback.
   */
  const hadSessionOnMount = useRef(state.auth);

  useEffect(() => {
    if (signinRedirectCallback) {
      signinRedirectCallback()
        .then(async (res: User) => {
          const profile = res.profile;
          // Fallback: sessionScopedStore already isolated the session at oidc-client's write.
          // Repeat here in case that intercept missed a payload shape; idempotent, since a
          // second call finds the flag set and the keys already moved. Resolves the claim the
          // same way the adapter does — profile then id token — or a profile-less payload the
          // adapter isolated would fall to the else branch and be migrated back to durable.
          if (isPublicComputerUser(res)) {
            // Fail closed: if the session can't be isolated, dispatching auth state would let
            // UserProvider persist the refresh token durably. Abort the login instead —
            // purging every durable credential, since oidc-client may have already written
            // the refresh token durably before the intercept.
            if (!beginPublicSession()) {
              purgeDurableCredentials();
              dispatch({ type: UserActionTypes.Reset });
              navigate('/login', { replace: true });
              return;
            }
          } else {
            // Ordinary login supersedes an earlier public one — else its marker costs every
            // later cold start an extra logout round trip.
            clearLastSessionPublic();
            // And returns this tab to durable storage if it ran a public session.
            endPublicSession();
          }
          // Persist the opaque OIDC sub claim so Sentry (and other
          // non-PII consumers) can identify the user without email.
          if (profile.sub) {
            writeSessionItem('userId', profile.sub);
          } else {
            removeSessionItem('userId');
          }
          // The idp is round-tripped through the OIDC `state` by `signinRedirect`. Set it here so it is
          // authoritative on every login, rather than relying on the pre-redirect app state surviving.
          const idp = getIdpFromState(res.state) ?? DEFAULT_IDP;
          dispatch({
            type: UserActionTypes.UpdateLoginStatus,
            payload: 'schema',
          });
          dispatch({
            type: UserActionTypes.UpdateUser,
            payload: {
              auth: true,
              idp,
              isDefaultIdp: idp === DEFAULT_IDP,
              user: {
                preferred_username: profile.preferred_username,
                schemas: profile['custom:schema'],
                superuser: profile['custom:superuser'],
              },
              tokens: {
                id_token: res.id_token,
                access_token: res.access_token,
                refresh_token: res.refresh_token,
                expires_at: res.expires_at,
                auth_time: profile.auth_time,
              },
            },
          });
        })
        .catch(() => {
          if (hadSessionOnMount.current) {
            // Stale replay over a good session — leave it intact. The effect below routes
            // the user on, and a genuinely dead session still logs out on the next 401.
            return;
          }

          // Session-scoped: a public-computer session must not leave UserState behind in
          // localStorage, and removeSessionItem clears whichever store holds it.
          removeSessionItem('UserState');
          dispatch({
            type: UserActionTypes.Reset,
          });
          // `/auth/cb` is a public route, so nothing else moves the user off it. Without this
          // the loader spins forever on a failed login. `Reset` clears idp and loginStatus, so
          // the login page renders its form rather than redirecting straight back out.
          navigate('/login', { replace: true });
        });
    }
  }, [signinRedirectCallback, dispatch, navigate]);

  useEffect(() => {
    if (state.auth) {
      // set navigation back to login page to handle schema selection.
      navigate('/login', { replace: true });
    }
  }, [state, navigate]);

  return <LoginLoader title={t('loading-user-data')} />;
};
export default LoginCallback;
